import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { dt, inr } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Loader2, Check, X, Paperclip, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";

const STATUS = { open: "bg-warning/15 text-warning", resolved: "bg-success/15 text-success", rejected: "bg-destructive/15 text-destructive" };

export default function Disputes() {
  const { user } = useAuth();
  const canReview = ["super_admin", "admin"].includes(user.role);
  const canRaise = ["merchant", "distributor"].includes(user.role);
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ order_id: "", reason: "", evidence: null, evidence_name: "" });
  const [saving, setSaving] = useState(false);
  const [viewer, setViewer] = useState(null); // {src, name}

  const load = useCallback(() => {
    api.get("/disputes").then((r) => setRows(r.data)).catch(() => setRows([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image"); return; }
    if (file.size > 2 * 1024 * 1024) { toast.error("Image too large (max 2 MB)"); return; }
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, evidence: reader.result, evidence_name: file.name }));
    reader.readAsDataURL(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/disputes", { order_id: form.order_id.trim(), reason: form.reason, evidence: form.evidence, evidence_name: form.evidence_name });
      toast.success("Dispute raised");
      setOpen(false); setForm({ order_id: "", reason: "", evidence: null, evidence_name: "" });
      load();
    } catch (e2) { toast.error(apiError(e2.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const viewEvidence = async (id) => {
    try {
      const { data } = await api.get(`/disputes/${id}/evidence`);
      setViewer({ src: data.evidence, name: data.evidence_name });
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const viewResolution = async (id) => {
    try {
      const { data } = await api.get(`/disputes/${id}/resolution-evidence`);
      setViewer({ src: data.evidence, name: data.evidence_name });
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const [resolveFor, setResolveFor] = useState(null);
  const [rform, setRform] = useState({ note: "", evidence: null, evidence_name: "" });

  const onResolveFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image"); return; }
    if (file.size > 2 * 1024 * 1024) { toast.error("Image too large (max 2 MB)"); return; }
    const reader = new FileReader();
    reader.onload = () => setRform((f) => ({ ...f, evidence: reader.result, evidence_name: file.name }));
    reader.readAsDataURL(file);
  };

  const doResolve = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch(`/disputes/${resolveFor}`, { action: "resolve", note: rform.note, evidence: rform.evidence, evidence_name: rform.evidence_name });
      toast.success("Dispute resolved");
      setResolveFor(null); setRform({ note: "", evidence: null, evidence_name: "" });
      load();
    } catch (e2) { toast.error(apiError(e2.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const review = async (id, action) => {
    try {
      await api.patch(`/disputes/${id}`, { action });
      toast.success(`Dispute ${action}d`);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-2xl font-bold tracking-tight">Disputes</h2>
          <p className="text-sm text-muted-foreground">Flag a transaction for operations to investigate.</p>
        </div>
        {canRaise && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="raise-dispute-button"><Plus className="mr-1.5 h-4 w-4" /> Raise dispute</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Raise a dispute</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-4">
                <div className="space-y-1.5"><Label>Order ID</Label><Input required value={form.order_id} onChange={(e) => setForm({ ...form, order_id: e.target.value })} placeholder="WP251003ABCD" data-testid="dispute-order-input" /></div>
                <div className="space-y-1.5"><Label>Reason</Label><Input required value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Amount credited but marked pending" data-testid="dispute-reason-input" /></div>
                <div className="space-y-1.5">
                  <Label>Evidence screenshot (optional)</Label>
                  <label className="flex items-center gap-2 rounded-xl border border-dashed px-3 py-2.5 text-sm text-muted-foreground cursor-pointer hover:bg-muted/40" data-testid="dispute-evidence-label">
                    <Paperclip className="h-4 w-4" />
                    <span className="flex-1 truncate">{form.evidence_name || "Attach an image (max 2 MB)"}</span>
                    <input type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="dispute-evidence-input" />
                  </label>
                  {form.evidence && <img src={form.evidence} alt="preview" className="max-h-32 rounded-lg border" />}
                </div>
                <DialogFooter><Button type="submit" disabled={saving} data-testid="dispute-submit-button">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit dispute"}</Button></DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>
      <Card className="p-4">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Created</TableHead>
                <TableHead>Order</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                {canReview && <TableHead>Raised by</TableHead>}
                <TableHead>Reason</TableHead>
                <TableHead>Evidence</TableHead>
                <TableHead>Status</TableHead>
                {canReview && <TableHead className="text-right">Action</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows === null && <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">Loading…</TableCell></TableRow>}
              {rows && rows.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">No disputes.</TableCell></TableRow>}
              {rows && rows.map((d) => (
                <TableRow key={d.id} data-testid={`dispute-row-${d.id}`}>
                  <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{dt(d.created_at)}</TableCell>
                  <TableCell className="font-mono text-xs">{d.order_id}</TableCell>
                  <TableCell className="text-right font-mono text-xs">{inr(d.amount)}</TableCell>
                  {canReview && <TableCell className="text-sm">{d.raised_by_name}</TableCell>}
                  <TableCell className="text-sm max-w-[240px] truncate">{d.reason}</TableCell>
                  <TableCell>
                    {d.has_evidence
                      ? <Button size="sm" variant="outline" className="h-7" onClick={() => viewEvidence(d.id)} data-testid={`view-evidence-${d.id}`}><ImageIcon className="mr-1 h-3.5 w-3.5" /> View</Button>
                      : <span className="text-xs text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell><Badge className={STATUS[d.status]}>{d.status}</Badge>
                    {d.resolved_by_name && <div className="text-[11px] text-muted-foreground mt-0.5">by {d.resolved_by_name}</div>}
                    {d.admin_note && <div className="text-[11px] text-muted-foreground mt-0.5 italic max-w-[200px] truncate">“{d.admin_note}”</div>}
                    {d.has_resolution_evidence && <button onClick={() => viewResolution(d.id)} className="text-[11px] text-primary underline mt-0.5" data-testid={`view-resolution-${d.id}`}>View proof</button>}
                  </TableCell>
                  {canReview && (
                    <TableCell className="text-right">
                      {d.status === "open" ? (
                        <div className="flex justify-end gap-1.5">
                          <Button size="icon" variant="outline" className="h-8 w-8 text-success" onClick={() => setResolveFor(d.id)} data-testid={`resolve-dispute-${d.id}`}><Check className="h-4 w-4" /></Button>
                          <Button size="icon" variant="outline" className="h-8 w-8 text-destructive" onClick={() => review(d.id, "reject")} data-testid={`reject-dispute-${d.id}`}><X className="h-4 w-4" /></Button>
                        </div>
                      ) : <span className="text-xs text-muted-foreground">—</span>}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Dialog open={!!resolveFor} onOpenChange={(o) => !o && setResolveFor(null)}>
        <DialogContent data-testid="resolve-dialog">
          <DialogHeader><DialogTitle>Resolve dispute</DialogTitle></DialogHeader>
          <form onSubmit={doResolve} className="space-y-4">
            <div className="space-y-1.5"><Label>Resolution note</Label><Input value={rform.note} onChange={(e) => setRform({ ...rform, note: e.target.value })} placeholder="How was this resolved?" data-testid="resolve-note-input" /></div>
            <div className="space-y-1.5">
              <Label>Proof screenshot (optional)</Label>
              <label className="flex items-center gap-2 rounded-xl border border-dashed px-3 py-2.5 text-sm text-muted-foreground cursor-pointer hover:bg-muted/40">
                <Paperclip className="h-4 w-4" /><span className="flex-1 truncate">{rform.evidence_name || "Attach an image (max 2 MB)"}</span>
                <input type="file" accept="image/*" className="hidden" onChange={onResolveFile} data-testid="resolve-evidence-input" />
              </label>
              {rform.evidence && <img src={rform.evidence} alt="preview" className="max-h-32 rounded-lg border" />}
            </div>
            <DialogFooter><Button type="submit" disabled={saving} data-testid="resolve-submit-button">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Resolve dispute"}</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!viewer} onOpenChange={(o) => !o && setViewer(null)}>
        <DialogContent data-testid="evidence-viewer">
          <DialogHeader><DialogTitle>Dispute evidence</DialogTitle></DialogHeader>
          {viewer && <img src={viewer.src} alt={viewer.name || "evidence"} className="w-full rounded-lg border" />}
          {viewer?.name && <p className="text-xs text-muted-foreground text-center">{viewer.name}</p>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
